import React, { useState, useEffect } from 'react'
import { IndianRupee, Zap, TrendingUp, Calendar } from 'lucide-react'
import revenueForecastService from '../../services/revenueForecastService'
import './RevenueForecastPanel.css'

const getCurrentMonth = () => {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0)
}

const formatDateShort = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const SkeletonCard = () => (
  <div className="metric-card revenue revenue-forecast-skeleton">
    <div className="metric-header">
      <div className="metric-icon skeleton-icon" />
    </div>
    <div className="metric-value skeleton-text" style={{ width: '80%' }} />
    <div className="metric-title skeleton-text" style={{ width: '60%', marginTop: 8 }} />
    <div className="metric-subtitle skeleton-text" style={{ width: '90%', marginTop: 4 }} />
  </div>
)

const RevenueForecastPanel = ({
  month: controlledMonth,
  onMonthChange,
  compact = false
}) => {
  const [month, setMonth] = useState(controlledMonth ?? getCurrentMonth())
  const [forecastWindowDays, setForecastWindowDays] = useState(90)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const effectiveMonth = controlledMonth ?? month

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await revenueForecastService.getRevenueForecast({
          month: effectiveMonth,
          forecastWindowDays
        })
        const payload = res?.data ?? res
        if (!cancelled && payload && typeof payload === 'object') {
          setData(payload)
        } else if (!cancelled) {
          setData(null)
        }
      } catch (e) {
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [effectiveMonth, forecastWindowDays])

  const handleMonthChange = (e) => {
    const v = e.target.value
    setMonth(v)
    onMonthChange?.(v)
  }

  const burn = data?.monthlyBurn ?? 0
  const burnTrailing = data?.burnTrailing30Days ?? 0
  const runwayMonthly = data?.projectedRunway90Days
  const runwayTrailing = data?.projectedRunway90DaysTrailing
  const hasBurnMonthly = burn > 0
  const hasBurnTrailing = burnTrailing > 0

  return (
    <div className="revenue-forecast-panel">
      <div className="section-header d-flex flex-wrap justify-content-between align-items-center gap-2" style={{ marginBottom: '12px' }}>
        <h3 className="mb-0">Revenue Forecast</h3>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <label className="d-flex align-items-center gap-2">
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #5B6478)' }}>Burn month</span>
            <input
              type="month"
              className="form-control form-control-sm"
              style={{ width: '140px' }}
              value={effectiveMonth}
              onChange={handleMonthChange}
              disabled={!!controlledMonth}
            />
          </label>
          <label className="d-flex align-items-center gap-2">
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #5B6478)' }}>Window</span>
            <select
              className="form-select form-select-sm"
              style={{ width: '90px' }}
              value={forecastWindowDays}
              onChange={(e) => setForecastWindowDays(Number(e.target.value))}
            >
              <option value={30}>30</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
              <option value={120}>120</option>
            </select>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted, #8B93A7)' }}>days</span>
          </label>
        </div>
      </div>

      {data?.forecastFrom && data?.forecastTo && (
        <div style={{ marginBottom: '12px', fontSize: '0.875rem', color: 'var(--color-text-secondary, #5B6478)' }}>
          Forecast window: <strong>{formatDateShort(data.forecastFrom)}</strong>
          {' → '}
          <strong>{formatDateShort(data.forecastTo)}</strong>
        </div>
      )}

      {loading ? (
        <div className="dashboard-metrics-grid">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : !data ? (
        <div className="revenue-forecast-empty">
          <Calendar size={32} strokeWidth={1.5} />
          <p>Unable to load revenue forecast</p>
        </div>
      ) : (
        <>
          <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon"><IndianRupee size={16} /></div>
              </div>
              <div className="metric-value">{formatCurrency(data.next30DaysExpected)}</div>
              <div className="metric-title">Next 30 Days Expected</div>
              <div className="metric-subtitle">Opportunities closing in 30 days</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon"><TrendingUp size={16} /></div>
              </div>
              <div className="metric-value">{formatCurrency(data.optimisticForecast)}</div>
              <div className="metric-title">{forecastWindowDays} Days Optimistic</div>
              <div className="metric-subtitle">Sum of all expected values</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon"><IndianRupee size={16} /></div>
              </div>
              <div className="metric-value">{formatCurrency(data.probabilityAdjustedForecast)}</div>
              <div className="metric-title">{forecastWindowDays} Days Weighted</div>
              <div className="metric-subtitle">Probability-adjusted forecast</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon"><Zap size={16} /></div>
              </div>
              <div className="metric-value revenue-forecast-runway">
                <div>
                  {hasBurnMonthly ? `${runwayMonthly} mo` : '—'}
                  {!hasBurnMonthly && <span className="badge bg-secondary ms-1">UNKNOWN</span>}
                </div>
                <div className="runway-trailing">
                  {hasBurnTrailing ? `${runwayTrailing} mo` : '—'}
                  {hasBurnTrailing && <span className="badge bg-info ms-1">Reality</span>}
                  {!hasBurnTrailing && burnTrailing === 0 && <span className="badge bg-secondary ms-1">UNKNOWN</span>}
                </div>
              </div>
              <div className="metric-title">Projected Runway (90 days)</div>
              <div className="metric-subtitle">
                a) Monthly burn · b) Trailing 30-day burn
              </div>
            </div>
          </div>

          <div style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--color-text-secondary, #5B6478)' }}>
            Conservative forecast (Weighted × 0.7): <strong>{formatCurrency(data.conservativeForecast)}</strong>
          </div>
        </>
      )}
    </div>
  )
}

export default RevenueForecastPanel
